import {
  collection,
  doc,
  setDoc,
  updateDoc,
  getDocs,
  query,
  where,
  orderBy,
  onSnapshot,
  Timestamp,
  limit as firestoreLimit,
} from "firebase/firestore";
import { getFirebaseDB } from "./firebase";
import { PropertyInquiry, InquiryStatus } from "@/types/types";

/**
 * Submit a property inquiry
 */
export async function submitPropertyInquiry(data: {
  propertyId: string;
  propertyTitle: string;
  landlordId: string;
  landlordName: string;
  landlordEmail: string;
  inquirerName: string;
  inquirerEmail: string;
  inquirerPhone?: string;
  message: string;
  moveInDate?: string;
}): Promise<string> {
  const db = getFirebaseDB();
  if (!db) throw new Error("Firebase not initialized");

  // Validation
  if (!data.message || data.message.trim().length < 10) {
    throw new Error("Message must be at least 10 characters");
  }

  if (data.message.length > 1000) {
    throw new Error("Message must be less than 1000 characters");
  }

  if (!data.inquirerName || data.inquirerName.trim().length < 2) {
    throw new Error("Name must be at least 2 characters");
  }

  if (!data.inquirerEmail || !data.inquirerEmail.includes("@")) {
    throw new Error("Valid email is required");
  }

  const inquiriesRef = collection(db, "property_inquiries");
  const inquiryRef = doc(inquiriesRef);
  const now = Timestamp.now().toDate().toISOString();

  const inquiryData: PropertyInquiry = {
    id: inquiryRef.id,
    propertyId: data.propertyId,
    propertyTitle: data.propertyTitle,
    landlordId: data.landlordId,
    landlordName: data.landlordName,
    landlordEmail: data.landlordEmail,
    inquirerName: data.inquirerName.trim(),
    inquirerEmail: data.inquirerEmail.trim().toLowerCase(),
    inquirerPhone: data.inquirerPhone?.trim(),
    message: data.message.trim(),
    moveInDate: data.moveInDate,
    status: "new",
    createdAt: now,
  };

  await setDoc(inquiryRef, inquiryData);

  return inquiryRef.id;
}

/**
 * Get inquiries for a landlord
 */
export async function getLandlordInquiries(
  landlordId: string,
  statusFilter?: InquiryStatus,
  limitCount: number = 100
): Promise<PropertyInquiry[]> {
  const db = getFirebaseDB();
  if (!db) throw new Error("Firebase not initialized");

  const inquiriesRef = collection(db, "property_inquiries");
  let q = query(
    inquiriesRef,
    where("landlordId", "==", landlordId),
    orderBy("createdAt", "desc"),
    firestoreLimit(limitCount)
  );

  if (statusFilter) {
    q = query(
      inquiriesRef,
      where("landlordId", "==", landlordId),
      where("status", "==", statusFilter),
      orderBy("createdAt", "desc"),
      firestoreLimit(limitCount)
    );
  }

  const snapshot = await getDocs(q);

  return snapshot.docs.map((doc) => ({
    id: doc.id,
    ...doc.data(),
  })) as PropertyInquiry[];
}

/**
 * Get inquiries for a specific property
 */
export async function getPropertyInquiries(
  propertyId: string,
  limitCount: number = 50
): Promise<PropertyInquiry[]> {
  const db = getFirebaseDB();
  if (!db) throw new Error("Firebase not initialized");

  const inquiriesRef = collection(db, "property_inquiries");
  const q = query(
    inquiriesRef,
    where("propertyId", "==", propertyId),
    orderBy("createdAt", "desc"),
    firestoreLimit(limitCount)
  );

  const snapshot = await getDocs(q);

  return snapshot.docs.map((doc) => ({
    id: doc.id,
    ...doc.data(),
  })) as PropertyInquiry[];
}

/**
 * Reply to an inquiry
 */
export async function replyToInquiry(
  inquiryId: string,
  landlordReply: string
): Promise<void> {
  const db = getFirebaseDB();
  if (!db) throw new Error("Firebase not initialized");

  if (!landlordReply || landlordReply.trim().length < 10) {
    throw new Error("Reply must be at least 10 characters");
  }

  if (landlordReply.length > 1000) {
    throw new Error("Reply must be less than 1000 characters");
  }

  const inquiryRef = doc(db, "property_inquiries", inquiryId);

  await updateDoc(inquiryRef, {
    landlordReply: landlordReply.trim(),
    repliedAt: Timestamp.now().toDate().toISOString(),
    status: "replied",
  });
}

/**
 * Update inquiry status
 */
export async function updateInquiryStatus(
  inquiryId: string,
  status: InquiryStatus
): Promise<void> {
  const db = getFirebaseDB();
  if (!db) throw new Error("Firebase not initialized");

  const inquiryRef = doc(db, "property_inquiries", inquiryId);

  await updateDoc(inquiryRef, {
    status,
  });
}

/**
 * Subscribe to landlord inquiries (real-time)
 */
export function subscribeToLandlordInquiries(
  landlordId: string,
  callback: (inquiries: PropertyInquiry[]) => void,
  errorCallback?: (error: Error) => void,
  statusFilter?: InquiryStatus
): () => void {
  const db = getFirebaseDB();
  if (!db) {
    errorCallback?.(new Error("Firebase not initialized"));
    return () => {};
  }

  const inquiriesRef = collection(db, "property_inquiries");
  let q = query(
    inquiriesRef,
    where("landlordId", "==", landlordId),
    orderBy("createdAt", "desc")
  );

  if (statusFilter) {
    q = query(
      inquiriesRef,
      where("landlordId", "==", landlordId),
      where("status", "==", statusFilter),
      orderBy("createdAt", "desc")
    );
  }

  const unsubscribe = onSnapshot(
    q,
    (snapshot) => {
      const inquiries = snapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      })) as PropertyInquiry[];
      callback(inquiries);
    },
    (error) => {
      console.error("Error subscribing to inquiries:", error);
      errorCallback?.(error);
    }
  );

  return unsubscribe;
}

/**
 * Get inquiry statistics for landlord
 */
export async function getInquiryStats(landlordId: string): Promise<{
  total: number;
  new: number;
  replied: number;
  closed: number;
}> {
  const inquiries = await getLandlordInquiries(landlordId);

  return {
    total: inquiries.length,
    new: inquiries.filter((i) => i.status === "new").length,
    replied: inquiries.filter((i) => i.status === "replied").length,
    closed: inquiries.filter((i) => i.status === "closed").length,
  };
}
